import { useState, useCallback } from 'react';
import { Player } from '../types';

interface ModalState {
  // Help modals
  showHelpModal: boolean;
  showContextualHelp: boolean;
  
  // Join modals
  showJoinModal: boolean;
  showJoinOptionsModal: boolean;
  showJoinOptionsForNewUser: boolean;
  hasUserClosedModal: boolean;
  
  // Report modals
  showReportModal: boolean;
  showSelfReportModal: boolean;
  showValidationModal: boolean;
  
  // Admin modals
  showTransferAdminModal: boolean;
  showEditLocationModal: boolean;
  showEditPlayerModal: boolean;
  showEditTeamNamesModal: boolean;
  showFinalScoreModal: boolean;
  showImportListModal: boolean;
  
  // Review modals
  showCreateReviewModal: boolean;
  showPlayerReviewsModal: boolean;
  showPlayerProfileModal: boolean;
  showReviewsList: boolean;
  
  // Other modals
  showVoteModal: boolean;
  showTacticalField: boolean;
  showShareModal: boolean;

  // Guest modals
  showGuestRequestsModal: boolean;
  showManageGuestsModal: boolean;

  // Invite modal
  showInvitePlayerModal: boolean;
}

interface ModalActions {
  // Help modals
  setShowHelpModal: (show: boolean) => void;
  setShowContextualHelp: (show: boolean) => void;
  
  // Join modals
  setShowJoinModal: (show: boolean) => void;
  setShowJoinOptionsModal: (show: boolean) => void;
  setShowJoinOptionsForNewUser: (show: boolean) => void;
  setHasUserClosedModal: (show: boolean) => void;
  closeJoinModal: () => void;
  
  // Report modals
  setShowReportModal: (show: boolean) => void;
  setShowSelfReportModal: (show: boolean) => void;
  setShowValidationModal: (show: boolean) => void;
  
  // Admin modals
  setShowTransferAdminModal: (show: boolean) => void;
  setShowEditLocationModal: (show: boolean) => void;
  setShowEditPlayerModal: (show: boolean) => void;
  setShowEditTeamNamesModal: (show: boolean) => void;
  setShowFinalScoreModal: (show: boolean) => void;
  setShowImportListModal: (show: boolean) => void;
  
  // Review modals
  setShowCreateReviewModal: (show: boolean) => void;
  setShowPlayerReviewsModal: (show: boolean) => void;
  setShowPlayerProfileModal: (show: boolean) => void;
  setShowReviewsList: (show: boolean) => void;
  
  // Other modals
  setShowVoteModal: (show: boolean) => void;
  setShowTacticalField: (show: boolean) => void;
  setShowShareModal: (show: boolean) => void;

  // Guest modals
  setShowGuestRequestsModal: (show: boolean) => void;
  setShowManageGuestsModal: (show: boolean) => void;

  // Invite modal
  setShowInvitePlayerModal: (show: boolean) => void;

  // Utility functions
  closeAllModals: () => void;
  resetModalState: () => void;
}

export const useModalManagement = (): ModalState & ModalActions => {
  // Help modals
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showContextualHelp, setShowContextualHelp] = useState(false);
  
  // Join modals
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showJoinOptionsModal, setShowJoinOptionsModal] = useState(false);
  const [showJoinOptionsForNewUser, setShowJoinOptionsForNewUser] = useState(false);
  const [hasUserClosedModal, setHasUserClosedModal] = useState(false);
  
  // Report modals
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSelfReportModal, setShowSelfReportModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  
  // Admin modals
  const [showTransferAdminModal, setShowTransferAdminModal] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [showEditPlayerModal, setShowEditPlayerModal] = useState(false);
  const [showEditTeamNamesModal, setShowEditTeamNamesModal] = useState(false);
  const [showFinalScoreModal, setShowFinalScoreModal] = useState(false);
  const [showImportListModal, setShowImportListModal] = useState(false);
  
  // Review modals
  const [showCreateReviewModal, setShowCreateReviewModal] = useState(false);
  const [showPlayerReviewsModal, setShowPlayerReviewsModal] = useState(false);
  const [showPlayerProfileModal, setShowPlayerProfileModal] = useState(false);
  const [showReviewsList, setShowReviewsList] = useState(false);
  
  // Other modals
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [showTacticalField, setShowTacticalField] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  // Guest modals
  const [showGuestRequestsModal, setShowGuestRequestsModal] = useState(false);
  const [showManageGuestsModal, setShowManageGuestsModal] = useState(false);

  // Invite modal
  const [showInvitePlayerModal, setShowInvitePlayerModal] = useState(false);

  const closeJoinModal = useCallback(() => {
    setShowJoinModal(false);
    setShowJoinOptionsModal(false);
    setShowJoinOptionsForNewUser(false);
    setHasUserClosedModal(true);
  }, []);

  const closeAllModals = useCallback(() => {
    setShowHelpModal(false);
    setShowContextualHelp(false);
    setShowJoinModal(false);
    setShowJoinOptionsModal(false);
    setShowJoinOptionsForNewUser(false);
    setShowReportModal(false);
    setShowSelfReportModal(false);
    setShowValidationModal(false);
    setShowTransferAdminModal(false);
    setShowEditLocationModal(false);
    setShowEditPlayerModal(false);
    setShowEditTeamNamesModal(false);
    setShowFinalScoreModal(false);
    setShowImportListModal(false);
    setShowCreateReviewModal(false);
    setShowPlayerReviewsModal(false);
    setShowPlayerProfileModal(false);
    setShowReviewsList(false);
    setShowVoteModal(false);
    setShowGuestRequestsModal(false);
    setShowManageGuestsModal(false);
    setShowInvitePlayerModal(false);
  }, []);

  const resetModalState = useCallback(() => {
    closeAllModals();
    // No resetear hasUserClosedModal aquí para evitar loops
    // setHasUserClosedModal(false);
  }, [closeAllModals]);

  return {
    // State
    showHelpModal,
    showContextualHelp,
    showJoinModal,
    showJoinOptionsModal,
    showJoinOptionsForNewUser,
    hasUserClosedModal,
    showReportModal,
    showSelfReportModal,
    showValidationModal,
    showTransferAdminModal,
    showEditLocationModal,
    showEditPlayerModal,
    showEditTeamNamesModal,
    showFinalScoreModal,
    showImportListModal,
    showCreateReviewModal,
    showPlayerReviewsModal,
    showPlayerProfileModal,
    showReviewsList,
    showVoteModal,
    showTacticalField,
    showShareModal,
    showGuestRequestsModal,
    showManageGuestsModal,
    showInvitePlayerModal,

    // Actions
    setShowHelpModal,
    setShowContextualHelp,
    setShowJoinModal,
    setShowJoinOptionsModal,
    setShowJoinOptionsForNewUser,
    setHasUserClosedModal,
    closeJoinModal,
    setShowReportModal,
    setShowSelfReportModal,
    setShowValidationModal,
    setShowTransferAdminModal,
    setShowEditLocationModal,
    setShowEditPlayerModal,
    setShowEditTeamNamesModal,
    setShowFinalScoreModal,
    setShowImportListModal,
    setShowCreateReviewModal,
    setShowPlayerReviewsModal,
    setShowPlayerProfileModal,
    setShowReviewsList,
    setShowVoteModal,
    setShowTacticalField,
    setShowShareModal,
    setShowGuestRequestsModal,
    setShowManageGuestsModal,
    setShowInvitePlayerModal,
    closeAllModals,
    resetModalState
  };
};
